import { useSetToast } from "@app/components/Toast";
import { useNavigation } from "@app/lib/navigation";
import {
    useDeleteObjective,
    useUpdateObjective,
} from "@app/screens/Objectives/data";
import UserChipSelect from "@app/shared/components/UserChipSelect";
import { UserTag } from "@app/shared/components/UserTag";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { currentUserAtom } from "@app/store/userStore";
import { displayStatus } from "@app/usecases/ObjectivesUseCases";
import { StatusTagCircular } from "@design-system/DataDisplay/StatusTagCircular";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import {
    type GetObjectiveQuery,
    ResponseStatus,
    type UpdateObjectiveMutation,
    UserRole,
} from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { type FC } from "react";
import { AutosaveDisplay } from "../../../../../shared/components/AutosaveDisplay/AutosaveDisplay";
import { useUpdateObjectiveStatus } from "../../data";
import { useObjectiveHeaderTranslations } from "./i18n";

type ObjectiveHeaderProps = {
    objective: GetObjectiveQuery["objective"];
};

export const ObjectiveHeader: FC<ObjectiveHeaderProps> = ({ objective }) => {
    const { t, keys } = useObjectiveHeaderTranslations();
    const { setToastSuccess, setToastError } = useSetToast();

    const { data: currentUser } = useAtomValue(currentUserAtom);
    const isContributor =
        currentUser?.currentUser.permission?.role === UserRole.Contributor;

    const [ownerId, setOwnerId] = useOptimistic(objective.ownerId);

    const updateObjectiveMutation = useUpdateObjective(
        objective.id,
        objective.entityId,
    );
    const deleteObjectiveMutation = useDeleteObjective(objective);
    const { navigateFromHome } = useNavigation();

    const statuses = useUpdateObjectiveStatus();

    const updateEntityMutationOpts = {
        onSettled: (data: UpdateObjectiveMutation | undefined) => {
            if (data?.updateObjective?.status === ResponseStatus.Error) {
                setToastError(`${data.updateObjective.error?.message}`);
            } else if (
                data?.updateObjective?.status === ResponseStatus.Success
            ) {
                setToastSuccess(t(keys.success_update));
            }
        },
        onError: () => {
            setToastError(t(keys.error_update));
        },
    };

    const handleUserIdChange = (userId: string) => {
        const newOwnerId = userId ?? null;
        if (objective.ownerId === newOwnerId) return;
        setOwnerId(newOwnerId);
        updateObjectiveMutation.mutate(
            { id: objective.id, ownerId: newOwnerId },
            updateEntityMutationOpts,
        );
    };

    const handleDeleteObjective = () => {
        deleteObjectiveMutation.mutate(objective.id, {
            onSuccess: () => {
                navigateFromHome("objectives");
                setToastSuccess(t(keys.success_delete));
            },
            onError: () => {
                setToastError(t(keys.error_delete));
            },
        });
    };

    return (
        <FlexRow
            as="header"
            className="border-b border-tertiary"
            justifyContent="between"
            alignItems="center"
            p="4"
        >
            <FlexRow gap="2.5" alignItems="center">
                <StatusTagCircular
                    size="lg"
                    status={displayStatus(objective.status)}
                />
                <Text>
                    {t(keys.status, {
                        context: objective.status,
                    })}
                </Text>
                <AutosaveDisplay statuses={statuses} />
            </FlexRow>
            <FlexRow gap="2" alignItems="center">
                {!isContributor && (
                    <UserChipSelect
                        userId={ownerId}
                        setUserId={handleUserIdChange}
                        trigger={
                            <UserTag
                                userId={objective.ownerId || undefined}
                                noUserText={t(keys.unassigned)}
                                size="md"
                                isTrigger
                            />
                        }
                        disabled={isContributor}
                    />
                )}

                <Dropdown>
                    <Dropdown.Trigger asChild>
                        <Button variant="text" size="sm">
                            <Button.Icon name="more" />
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Modal>
                            <Modal.Trigger>
                                <Dropdown.Item variant="negative">
                                    <Dropdown.ItemIcon name="trash" />
                                    <Dropdown.ItemText>
                                        {t(keys.delete)}
                                    </Dropdown.ItemText>
                                </Dropdown.Item>
                            </Modal.Trigger>
                            <Modal.Overlay>
                                <Modal.Content>
                                    <Modal.Header withClose>
                                        <Modal.Title>
                                            {t(keys.delete)}
                                        </Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {t(keys.delete_modal_description)}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Modal.CancelCTA>
                                            {t(keys.delete_cancel)}
                                        </Modal.CancelCTA>
                                        <Modal.ConfirmCTA
                                            variant="flatDanger"
                                            onClick={handleDeleteObjective}
                                        >
                                            {t(keys.delete_cta)}
                                        </Modal.ConfirmCTA>
                                    </Modal.Footer>
                                </Modal.Content>
                            </Modal.Overlay>
                        </Modal>
                    </Dropdown.Content>
                </Dropdown>
            </FlexRow>
        </FlexRow>
    );
};
